import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';

import WebsiteUtils from '../../shared/website-utils';
import {WebsiteModel} from '../../widgets/form-section/website-user-input/website.model';
import * as moment from 'moment';
import DateUtils from '../../shared/date-utils';


export class CustomValidators {
  public static referenceNumberPattern = '^([0-9]{5,7})$';
  public static identificatorNumberPattern = '^([0-9]{1,18})$';
  public static postalCodePattern = '^([0-9]{2}-[0-9]{3})$';
  public static numbersPattern = '^[0-9]+$';
  public static decimalNumbersPattern = '^\\d+([,.]\\d{1,2})?$';
  public static yearsNumbersPattern = '^\\d+([,.]\\d{1})?$';
  public static namePattern = '^[a-zA-ZĄąĆćĘęŁłŃńÓóŚśŹźŻż]+([\\- ][a-zA-ZĄąĆćĘęŁłŃńÓóŚśŹźŻż]+)*$';
  public static houseNumberFlatNumberPattern = '^[a-zA-Z0-9]+(\\/[a-zA-Z0-9]+)*$';
  public static streetNamePattern = '^[a-zA-ZĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9 \\-.,"]+$';
  public static phoneNumberPattern = '^[(]?\\+?[0-9 \\-()]+$';
  public static whiteSpacePattern = '\\s+';
  public static urlPattern = '^(http:\\/\\/|https:\\/\\/)?(www.)?([\\w]{1,})\\.{1}[\\w-]+\\.{0,1}[\\w]{1,}\\.{0,1}?([\\w\\?\\=\\&]+)?((\\/?([\\w+\\?&_=\\-#])\\/([\\w+&_=\\?\\-\\#])?)|(\\/([\\w+&\\?_=\\-#])?\\/?[a-zA-Z0-9&?_=\\-#\\/\\.]+)?)?$';
  public static referenceNumberRegex = new RegExp(CustomValidators.referenceNumberPattern);
  public static identificatorNumberRegex = new RegExp(CustomValidators.identificatorNumberPattern);
  public static postalCodeRegex = new RegExp(CustomValidators.postalCodePattern);
  public static numbersRegex = new RegExp(CustomValidators.numbersPattern);
  public static decimalNumbersRegex = new RegExp(CustomValidators.decimalNumbersPattern);
  public static yearsNumbersRegex = new RegExp(CustomValidators.yearsNumbersPattern);
  public static nameRegex = new RegExp(CustomValidators.namePattern);
  public static houseNumberFlatNumberRegex = new RegExp(CustomValidators.houseNumberFlatNumberPattern);
  public static streetNameRegex = new RegExp(CustomValidators.streetNamePattern);
  public static phoneNumberRegex = new RegExp(CustomValidators.phoneNumberPattern);
  public static whiteSpaceRegex = new RegExp(CustomValidators.whiteSpacePattern);
  public static urlRegex = new RegExp(CustomValidators.urlPattern);

  static validateCorrectDict(control: AbstractControl): { [key: string]: any } {
    return !control.value ? {'incorrectDictValue': true} : null;
  }

  static validateCorrectDictIfSet(control: AbstractControl): { [key: string]: any } {
    return control.value === false ? {'incorrectDictValue': true} : null;
  }

  static validateDictExists(control: AbstractControl): { [key: string]: any } {
    return !control.value ? {'dictDoesNotExistMsg': true} : null;
  }

  static validateNotEmptyEmail(control: AbstractControl): { [key: string]: any } {
    return !control.value ? null : Validators.email(control);
  }

  static correctWebsite(websiteEnum: WebsiteModel[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const newUrl = WebsiteUtils.removeWebsitePrefix(control.value);
      if (!newUrl) {
        return null;
      }

      const website = WebsiteUtils.getWebsiteFromEnum(newUrl, websiteEnum);
      if (website === null) {
        return {'incorrectWebsiteUrl': true};
      }

      const userName = WebsiteUtils.getUserNameFromWebsiteUrl(newUrl, website);
      if (userName.indexOf('/') > -1) {
        return {'incorrectUserNameInWebsiteUrl': true};
      }
      return null;
    };
  }

  static correctUsername(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value && control.value.indexOf('/') > -1) {
        return {'incorrectUserName': true};
      }
      return null;
    };
  }

  static validateReferenceNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      return !control.value || CustomValidators.referenceNumberRegex.test(control.value) ? null : {'incorrectReferenceNumber': true};
    };
  }

  static validateIdentificatorNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      return !control.value ||
      CustomValidators.identificatorNumberRegex.test(control.value) ? null : {'incorrectIdentificatorNumber': true};
    };
  }

  static validatePostalCode(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.postalCodeRegex.test(control.value) ? null : {'postalCode': true};
  }

  static validateJobOrderNumber(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.numbersRegex.test(control.value) ? null : {'jobOrderNumber': true};
  }

  static validateDates(dateFrom: string, dateTo: string): ValidatorFn {
    return (formGroup: FormGroup): { [key: string]: any } => {
      if (!formGroup.get(dateFrom).value || !formGroup.get(dateTo).value) {
        return null;
      }

      const startDateTimestamp = Date.parse(formGroup.get(dateFrom).value);
      const endDateTimestamp = Date.parse(formGroup.get(dateTo).value);

      if (!startDateTimestamp || !endDateTimestamp || (startDateTimestamp <= endDateTimestamp)) {
        return null;
      } else {
        return {'incorrectDate': true};
      }
    };
  }

  static noEmailOrPhoneNumberValidation(email: string, phoneNumber: string): ValidatorFn {
    return (formGroup: FormGroup): { [key: string]: any } => {
      const emailContent = formGroup.controls[email].value;
      const phoneNumberContent = formGroup.controls[phoneNumber].value;
      if (!emailContent && !phoneNumberContent) {
        return {'noPhoneOrEmailAddress': true};
      }
      return null;
    };
  }

  static validateDateFormats(control: AbstractControl): { [key: string]: any } {
    if (!control.value) {
      return null;
    }
    return DateUtils.validDateFormats
      .find(dateFormat => moment(control.value, dateFormat, true).isValid()) ? null : {'incorrectDateFormat': true};
  }

  static validateDateBeforeCurrentDay(control: AbstractControl): { [key: string]: any } {
    if (!control.value) {
      return null;
    }

    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getMonth();
    const currentDay = currentTime.getDate();

    const now = new Date(currentYear, currentMonth, currentDay, 0, 0, 0, 0);
    const startDateTimestamp = new Date(control.value);

    if (startDateTimestamp < now) {
      return {'incorrectFromDate': true};
    } else {
      return null;
    }
  }

  static validateNgbTimeStructDates(dateFrom: string, dateTo: string): ValidatorFn {
    return (formGroup: FormGroup): { [key: string]: any } => {
      if (!formGroup.get(dateFrom).value || !formGroup.get(dateTo).value) {
        return null;
      }

      const dateFromHour = formGroup.get(dateFrom).value.hour;
      const dateFromMinute = formGroup.get(dateFrom).value.minute;
      const dateToHour = formGroup.get(dateTo).value.hour;
      const dateToMinute = formGroup.get(dateTo).value.minute;

      if (dateFromHour == null || dateFromMinute == null || dateToHour == null || dateToMinute == null || (dateFromHour > dateToHour)) {
        return {'incorrectTime': true};
      } else {
        if (dateFromHour === dateToHour && (dateFromMinute > dateToMinute)) {
          return {'incorrectTime': true};
        } else {
          return null;
        }
      }
    };
  }

  static validateNewPasswordWithConfirmationPassword(newPassword: string, newPasswordConfirmation: string): ValidatorFn {
    return (formGroup: FormGroup): { [key: string]: any } => {
      if (!formGroup.get(newPassword).value || !formGroup.get(newPasswordConfirmation).value) {
        return null;
      }

      if (formGroup.get(newPassword).value === formGroup.get(newPasswordConfirmation).value) {
        return null;
      } else {
        return {'incorrectNewPassword': true};
      }
    };
  }

  static validateNewPassword(control: FormControl) {
    if (control.value && CustomValidators.whiteSpaceRegex.test(control.value)) {
      return {'incorrectPasswordContainsWhiteSpaces': true};
    }
    return null;
  }

  static notEmpty(control: AbstractControl): { [key: string]: any } {
    return !control.value || !control.value.toString().trim() ? {'required': true} : null;
  }

  static validateDecimal(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.decimalNumbersRegex.test(control.value) ? null : {'incorrectDecimal': true};
  }

  static validateYears(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.yearsNumbersRegex.test(control.value) ? null : {'incorrectYears': true};
  }

  static validateName(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.nameRegex.test(control.value) ? null : {'incorectName': true};
  }

  static validateHouseNumberFlatNumber(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.houseNumberFlatNumberRegex.test(control.value)
      ? null : {'incorectHouseNumberFlatNumber': true};
  }

  static validateStreetName(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.streetNameRegex.test(control.value) ? null : {'incorectStreetName': true};
  }

  static validatePhoneNumber(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.phoneNumberRegex.test(control.value) ? null : {'incorectPhoneNumber': true};
  }

  static validateUrl(control: AbstractControl): { [key: string]: any } {
    return !control.value || CustomValidators.urlRegex.test(control.value) ? null : {'incorrectUrl': true};
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/core/validator/custom-validators.ts